import { ReadonlyMat4, ReadonlyVec3, vec3 } from 'gl-matrix';
import { Vertex } from './slicemesh/Vertex';

/**
 * {@link Edge} represents a connection between two points in space.
 */
export class Edge {
    /**
     * The {@link start Starting} point of the {@link Edge}.
     */
    public start: Vertex;

    /**
     * The {@link end Ending} point of the {@link Edge}.
     */
    public end: Vertex;

    /**
     * Constructor that will create a connection in between two points.
     * @param start The {@link start Starting} point of the {@link Edge}.
     * @param end The {@link end Ending} point of the {@link Edge}.
     */
    public constructor(start: Vertex, end: Vertex) {
        this.start = start;
        this.end = end;
    }

    /**
     * Method to get vector from {@link start} to {@link end}.
     */
    private get delta(): vec3 {
        return vec3.subtract(
            vec3.create(),
            this.end.coordinates,
            this.start.coordinates
        );
    }

    /**
     * Method to calculate the length of this {@link Edge}.\
     * This is the length of a vector from the {@link start} to {@link end}.
     */
    public get length(): number {
        const delta = this.delta;
        return vec3.length(delta);
    }

    /**
     * Method to get the direction vector of this {@link Edge}.\
     * That's a vector with length of 1, pointing from {@link start} towards {@link end}.
     */
    public get direction(): vec3 {
        return vec3.normalize(vec3.create(), this.delta);
    }

    /**
     * Method to get the centerpoint of this {@link Edge}.\
     * It is the point in between {@link start} and {@link end}.
     */
    public get centerCoordinates(): vec3 {
        const sum = vec3.add(
            vec3.create(),
            this.start.coordinates,
            this.end.coordinates
        );
        return vec3.scale(sum, sum, 0.5);
    }

    /**
     * Method to get the center {@link Vertex}
     */
    public get centerVertex(): Vertex {
        return Vertex.lerp(this.start, this.end, 0.5);
    }

    /**
     * Method to get points distance to edge.
     * @param point Point the distance of which is to be determined
     * @returns Perpendicular distance to line if point is between the start and end points. Distance to closest end point otherwise.
     */
    public pointDistance(point: ReadonlyVec3): number {
        const direction = this.direction;
        const distToStart = vec3.subtract(
            vec3.create(),
            point,
            this.start.coordinates
        );
        const distToEnd = vec3.subtract(
            vec3.create(),
            point,
            this.end.coordinates
        );
        const distToStartAlongEdge = vec3.dot(distToStart, direction);
        const distToEndAlongEdge = vec3.dot(distToEnd, direction);
        if (Math.sign(distToStartAlongEdge) === Math.sign(distToEndAlongEdge)) {
            //point is outside of the line segments end points, meaning that closest point is either of the two end points.
            return Math.abs(distToStartAlongEdge) < Math.abs(distToEndAlongEdge)
                ? vec3.length(distToStart)
                : vec3.length(distToEnd);
        } else {
            //distance is perpendicular distance to line segment
            const closestPointOnLine = vec3.add(
                vec3.create(),
                vec3.scale(vec3.create(), direction, distToEndAlongEdge),
                this.end.coordinates
            );
            const deltaToClosestPointOnLine = vec3.length(
                vec3.subtract(vec3.create(), point, closestPointOnLine)
            );
            return deltaToClosestPointOnLine;
        }
    }

    /**
     * Computes the intersection of this edge with the XY plane at Z=0 in projection space.
     *
     * This method transforms the edge endpoints from patient/world space into projection space
     * using the provided transformation matrix. In projection space, the slice plane is always
     * positioned at Z=0, making intersection detection efficient (checking if Z changes sign).
     *
     * @param toProjection Transformation matrix from patient/world space to projection space.
     *                     This matrix should position the desired slice plane at Z=0 in the
     *                     transformed coordinate system.
     * @param singleSlice If true, returns start vertex when both endpoints lie exactly on the plane (Z=0).
     *                    If false, returns undefined in this case. Used for handling edges that lie
     *                    entirely within the slice plane (e.g., 2D contours on a single slice).
     * @returns The intersection {@link Vertex} in projection space if the edge crosses the Z=0 plane,
     *          or undefined if there is no intersection. The returned vertex includes interpolated
     *          texture coordinates based on the intersection position along the edge.
     */
    intersection(
        toProjection: ReadonlyMat4,
        singleSlice: boolean
    ): Vertex | undefined {
        const start = new Vertex(this.start).transform(toProjection);
        const end = new Vertex(this.end).transform(toProjection);
        let startDist = start.coordinates[2];
        let endDist = end.coordinates[2];
        if (Math.sign(startDist) !== Math.sign(endDist)) {
            //if start & end are on the other sides of the plane
            startDist = Math.abs(startDist);
            endDist = Math.abs(endDist);
            return Vertex.lerp(start, end, startDist / (startDist + endDist));
        } else if (Math.abs(startDist) < 1e-6 && Math.abs(endDist) > 1e-6) {
            // if start is on the plane and end is not
            return start;
        } else if (Math.abs(endDist) < 1e-6 && Math.abs(startDist) > 1e-6) {
            // if end is on the plane and start is not
            return end;
        } else if (startDist === 0 && endDist === 0 && singleSlice) {
            return start;
        }
        return undefined;
    }
}
